import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type Member = {
  user_id: string;
  role: 'admin' | 'editor';
  joined_at: number;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
};

export type Invitation = {
  id: string;
  email: string;
  role: 'admin' | 'editor';
  expires_at: number;
  created_at: number;
  accept_url: string;
};

export type SitePermission = {
  site_id: string;
  user_id: string;
  granted_at: number;
};

export function useMembers() {
  return useQuery({
    queryKey: ['team', 'members'],
    queryFn: () => api.get<{ members: Member[] }>('/api/app/team/members'),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.del<{ removed: boolean }>(`/api/app/team/members/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export function useChangeMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: string; role: 'admin' | 'editor' }) =>
      api.patch<{ updated: boolean; role: string }>(
        `/api/app/team/members/${args.userId}`,
        { role: args.role },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: ['team', 'invitations'],
    queryFn: () => api.get<{ invitations: Invitation[] }>('/api/app/team/invitations'),
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: 'admin' | 'editor' }) =>
      api.post<{ invitation: Invitation }>('/api/app/team/invitations', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', 'invitations'] }),
  });
}

export function useCancelInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.del<{ cancelled: boolean }>(`/api/app/team/invitations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', 'invitations'] }),
  });
}

export function useSitePermissions() {
  return useQuery({
    queryKey: ['team', 'permissions'],
    queryFn: () => api.get<{ permissions: SitePermission[] }>('/api/app/team/site-permissions'),
  });
}

export function useGrantSitePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { site_id: string; user_id: string }) =>
      api.post<{ granted: boolean }>('/api/app/team/site-permissions', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', 'permissions'] }),
  });
}

export function useRevokeSitePermission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { site_id: string; user_id: string }) =>
      api.del<{ revoked: boolean }>('/api/app/team/site-permissions').then(async () => {
        // DELETE with body not standard — use POST-style via fetch instead
        const res = await fetch('/api/app/team/site-permissions', {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('revoke failed');
        return { revoked: true };
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', 'permissions'] }),
  });
}
